import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Loader2, FolderTree } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { StatusFilterTabs } from "@/components/admin/StatusFilterTabs";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { category_name: "", category_code: "", description: "", image: "", status: "active", display_order: 0 };

export default function AdminCourseCategoriesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/course-categories", { params: { status_filter: statusFilter } });
      setItems(data.data.categories);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const lc = useLifecycleActions({
    basePath: "/admin/course-categories",
    labelOf: (row) => `"${row.category_name}"`,
    onDone: load,
  });

  const openCreate = () => { setForm(EMPTY); setDlg({ create: true }); };
  const openEdit = (c) => {
    setForm({ ...EMPTY, category_name: c.category_name, category_code: c.category_code,
      description: c.description || "", image: c.image || "",
      status: c.status, display_order: c.display_order || 0 });
    setDlg(c);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (dlg.create) {
        await api.post("/admin/course-categories", form);
        toast.success("Category created.");
      } else {
        const body = { category_name: form.category_name, description: form.description, image: form.image, status: form.status, display_order: form.display_order };
        await api.put(`/admin/course-categories/${dlg.id}`, body);
        toast.success("Category updated.");
      }
      setDlg(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Course categories" breadcrumbs={["Platform", "Courses", "Categories"]}>
      <div className="space-y-4" data-testid="admin-course-categories">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-slate-600 flex-1 min-w-[240px]">Group your healthcare courses into categories — NABH, Fire Safety, IPC, etc.</div>
          <StatusFilterTabs value={statusFilter} onChange={setStatusFilter} testId="categories-status-filter" />
          <button onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="admin-course-category-add">
            <Plus className="h-4 w-4" /> Add category
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading categories…" /> : items.length === 0 ? (
            <EmptyState title="No categories yet" description="Add your first category to organize courses." icon={FolderTree} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Order</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Code</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Courses</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-xs text-slate-500">{c.display_order}</TableCell>
                    <TableCell className="font-semibold text-slate-900">{c.category_name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{c.category_code}</TableCell>
                    <TableCell className="text-sm text-slate-700">{c.course_count || 0}</TableCell>
                    <TableCell><StatusBadge status={c.deleted_at ? "deleted" : c.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(c)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]"><Edit3 className="h-3.5 w-3.5" /></button>
                        <RowActionsMenu
                          testId={`category-actions-${c.id}`}
                          status={c.deleted_at ? "deleted" : c.status}
                          onView={() => openEdit(c)}
                          onEdit={() => openEdit(c)}
                          onDeactivate={() => lc.open("deactivate", c)}
                          onReactivate={() => lc.open("reactivate", c)}
                          onDelete={() => lc.open("delete", c)}
                          onRestore={() => lc.open("restore", c)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="admin-course-category-form">
          <DialogHeader><DialogTitle>{dlg?.create ? "Add category" : `Edit ${dlg?.category_name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={form.category_name} onChange={(e) => setForm({ ...form, category_name: e.target.value })} data-testid="category-name" /></div>
            {dlg?.create && (
              <div className="space-y-1"><Label>Code *</Label><Input value={form.category_code} onChange={(e) => setForm({ ...form, category_code: e.target.value.toUpperCase() })} placeholder="e.g. NABH" data-testid="category-code" /></div>
            )}
            <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Image URL (optional)</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" /></div>
              <div className="space-y-1"><Label>Display order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || "0", 10) })} /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="category-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {dlg?.create ? "Create" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {lc.dialog}
    </DashboardLayout>
  );
}
